import React, { useEffect, useRef, useState } from 'react';
import './Map.css';

let ymapsLoadPromise = null;

const RouteMap = ({ points = [], className = "" }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const multiRouteRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {

    const extractCoordinates = (pointsArray) => {
      if (!pointsArray || pointsArray.length === 0) return [];
      
      return pointsArray.map(point => {
        if (Array.isArray(point)) return point;
        if (point?.coords && Array.isArray(point.coords)) return point.coords;
        if (point?.lat && point?.lng) return [point.lat, point.lng];
        return null;
      }).filter(p => p !== null && p.length === 2);
    };

    const initMapAndRoute = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Проверка точек
        const routePoints = extractCoordinates(points);
        
        console.log('Точки для маршрута (координаты):', routePoints);
        
        if (routePoints.length < 2) {
          throw new Error(`Минимум 2 точки для маршрута. Получено: ${routePoints.length}`);
        }

        // 2. Загрузка api
        if (!ymapsLoadPromise) {
          ymapsLoadPromise = new Promise((resolve, reject) => {
            if (window.ymaps && window.ymaps.Map) {
              window.ymaps.ready(resolve);
              return;
            }

            const script = document.createElement('script');
            script.src = `https://api-maps.yandex.ru/2.1/?apikey=0d7303a8-7661-444f-b93c-0e7be5c4f3a1&lang=ru_RU&load=package.full`;
            script.async = true;
            
            script.onload = () => {
              window.ymaps.ready(resolve);
            };
            script.onerror = () => {
              reject(new Error('Не удалось загрузить API Яндекс.Карт'));
            };
            
            document.head.appendChild(script);
          });
        }

        await ymapsLoadPromise;
        
        if (!mapRef.current) return;

        const ymaps = window.ymaps;

        // 3. Проверка модуля маршрутизации
        if (!ymaps.multiRouter || !ymaps.multiRouter.MultiRoute) {
          throw new Error('Модуль маршрутизации не загружен');
        }

        // 4. Уничтожение старой карты
        if (mapInstance.current) {
          mapInstance.current.destroy();
          mapInstance.current = null;
        }

        // 5. Создание карты
        const map = new ymaps.Map(mapRef.current, {
          center: routePoints[0],  // Центрируем на первой точке маршрута
          zoom: 13,
          controls: ['zoomControl', 'typeSelector']
        });
        
        mapInstance.current = map;

        // 6. Создание маршрута
        const multiRoute = new ymaps.multiRouter.MultiRoute(
          {
            referencePoints: routePoints,
            params: {
              routingMode: 'pedestrian'
            }
          },
          {
            boundsAutoApply: true,
            wayPointVisible: true,
            routeActiveStrokeWidth: 5,
            routeActiveStrokeColor: '#1E88E5',
            routeStrokeWidth: 4,
            routeStrokeColor: '#1E88E5'
          }
        );
        
        multiRouteRef.current = multiRoute;

        // 7. Обработчик ошибок маршрута
        multiRoute.events.add('error', (e) => {
          const error = e.get('error');
          console.error('❌ Ошибка построения маршрута:', error);
          
          let errorMessage = 'Не удалось построить маршрут';
          if (error && error.message) {
            errorMessage = error.message;
          } else if (error && typeof error === 'string') {
            errorMessage = error;
          }
          
          setError(errorMessage);
        });

        // 8. Обраотчик успешного обновления
        multiRoute.events.add('update', () => {
          console.log('✅ Маршрут успешно построен!');
          
          try {
            const routes = multiRoute.getRoutes();
            if (routes && routes.length > 0) {
              const route = routes[0];
              const distance = route.properties.get('distance')?.text;
              const duration = route.properties.get('duration')?.text;
              console.log(`📏 Длина: ${distance}, ⏱️ Время: ${duration}`);
            }
            
            // Приближаем карту к маршруту
            const bounds = multiRoute.getBounds();
            if (bounds && mapInstance.current) {
              mapInstance.current.setBounds(bounds, {
                checkZoomRange: true,
                zoomMargin: 50
              });
            }
          } catch (err) {
            console.warn('Ошибка при получении информации о маршруте:', err);
          }
        });

        // 9. Добавление маршрута на карту
        map.geoObjects.add(multiRoute);

        // 10. Добавление маркеров
        points.forEach((point, index) => {
          let coords;
          let title;
          
          if (Array.isArray(point)) {
            coords = point;
            title = `Точка ${index + 1}`;
          } else if (point?.coords) {
            coords = point.coords;
            title = point.title || `Точка ${index + 1}`;
          } else if (point?.lat && point?.lng) {
            coords = [point.lat, point.lng];
            title = point.title || `Точка ${index + 1}`;
          } else {
            return;
          }
          
          if (coords && coords.length === 2) {
            const placemark = new ymaps.Placemark(
              coords,
              { balloonContent: title, hintContent: title },
              { preset: 'islands#blueCircleIcon', iconColor: '#1E88E5' }
            );
            map.geoObjects.add(placemark);
          }
        });

        setLoading(false);
        
        // 11. Таймаут для проверки
        setTimeout(() => {
          if (multiRouteRef.current) {
            const routes = multiRouteRef.current.getRoutes();
            if (!routes || routes.length === 0) {
              console.warn('⚠️ Маршрут не построен. Возможно, точки слишком близко или проблема с API.');
            }
          }
        }, 2000);

      } catch (err) {
        console.error('❌ Фатальная ошибка:', err);
        setError(err.message || 'Не удалось загрузить карту');
        setLoading(false);
      }
    };

    if (points && points.length >= 2) {
      initMapAndRoute();
    } else {
      setLoading(false);
      setError('Для построения маршрута нужно минимум 2 точки');
    }

    // Очистка при размонтировании
    return () => {
      if (multiRouteRef.current) {
        multiRouteRef.current = null;
      }
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    };
  }, [points]);

  // Рендер
  return (
    <div className={`mapWrapper flex-column ${className}`} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && (
        <div className="map-loading" style={{ 
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
          zIndex: 10, background: 'white', padding: '10px 20px', borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
        }}>
          Загрузка карты и построение маршрута...
        </div>
      )}

      {error && !loading && (
        <div className="map-error" style={{ 
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
          zIndex: 10, background: 'white', padding: '10px 20px', borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: 'red' 
        }}>
          ⚠️ {error}
        </div>
      )}
      
      <div 
        ref={mapRef} 
        className="map"
        style={{ width: '100%', height: '100%', minHeight: '500px' }}
      />
    </div>
  );
};

export default RouteMap;